import { useEffect } from 'react';
import { moveWithin } from './actions';
import { closeMenu } from './menu';

/**
 * Drag a stop into a new place on a route not started (control-room-web.html, "Drag a stop"): a copy of the row rides
 * under the pointer, the row it left keeps its place as a gap, and a line shows where it lands. Rows carry
 * `data-drag="routeId|stopId"`; a started route's stops don't (its order is the courier's). Esc cancels.
 */

interface Drag {
  route: string;
  stop: string;
  row: HTMLElement;
  x0: number;
  y0: number;
  live: boolean;
  list?: HTMLElement;
  card?: HTMLElement;
  line?: HTMLElement;
  dy?: number;
  at?: number;
}

let drag: Drag | null = null;
let swallow = false;

/** What rides under the pointer: a copy of the row; a table row needs a table of its own, with the same widths. */
function cardOf(row: HTMLElement): HTMLElement {
  const copy = row.cloneNode(true) as HTMLElement;
  if (row.tagName !== 'TR') return copy;
  const cells = [...(copy as HTMLTableRowElement).cells];
  [...(row as HTMLTableRowElement).cells].forEach((td, i) => {
    if (td.offsetWidth) cells[i].style.width = `${td.offsetWidth}px`;
    else cells[i].remove();
  });
  const table = document.createElement('table');
  table.appendChild(document.createElement('tbody')).appendChild(copy);
  return table;
}

function start(e: PointerEvent): boolean {
  const d = drag!;
  const rect = d.row.getBoundingClientRect();
  d.list = (d.row.closest('.list, .wide, .tw') as HTMLElement | null) ?? undefined;
  if (!d.list) return false;
  d.live = true;
  d.dy = e.clientY - rect.top;
  d.card = cardOf(d.row);
  d.card.classList.add('dragcard');
  Object.assign(d.card.style, { width: `${rect.width}px`, left: `${rect.left}px`, top: `${rect.top}px` });
  document.body.appendChild(d.card);
  d.row.classList.add('gap');
  d.line = document.createElement('div');
  d.line.className = 'dropline';
  const host = d.list.classList.contains('tw') ? (d.list.parentElement as HTMLElement) : d.list;
  if (getComputedStyle(host).position === 'static') host.style.position = 'relative';
  host.appendChild(d.line);
  document.body.classList.add('dragging');
  document.getElementById('tip')?.classList.remove('on');
  closeMenu();
  return true;
}

function move(e: PointerEvent) {
  const d = drag!;
  const list = d.list!;
  const lr = list.getBoundingClientRect();
  d.card!.style.top = `${e.clientY - d.dy!}px`;
  if (e.clientY < lr.top + 48) list.scrollTop -= 14;
  else if (e.clientY > lr.bottom - 48) list.scrollTop += 14;
  const rows = ([...list.querySelectorAll('[data-drag]')] as HTMLElement[]).filter((x) => x !== d.row && x.dataset.drag?.startsWith(`${d.route}|`));
  const before = rows.find((x) => {
    const b = x.getBoundingClientRect();
    return e.clientY < b.top + b.height / 2;
  });
  d.at = before ? rows.indexOf(before) : rows.length;
  const ref = before ?? rows.at(-1);
  const host = d.line!.parentElement!;
  const hr = host.getBoundingClientRect();
  if (ref) {
    const b = ref.getBoundingClientRect();
    d.line!.style.top = `${(before ? b.top : b.bottom) - hr.top + host.scrollTop - 1}px`;
    d.line!.hidden = false;
  } else d.line!.hidden = true;
}

function end(commit: boolean) {
  const d = drag;
  drag = null;
  if (!d?.live) return;
  d.card?.remove();
  d.line?.remove();
  d.row.classList.remove('gap');
  document.body.classList.remove('dragging');
  if (commit && d.at != null) {
    // Where it lands among the route's stops without it: that's the index the route takes.
    const rows = ([...d.list!.querySelectorAll('[data-drag]')] as HTMLElement[]).filter((x) => x.dataset.drag?.startsWith(`${d.route}|`));
    const from = rows.indexOf(d.row);
    if (d.at !== from) moveWithin(d.route, d.stop, d.at);
  }
}

let wired = false;

/** The document's drag listeners, set once. */
export function useDrag() {
  useEffect(() => {
    if (wired) return;
    wired = true;
    document.addEventListener('pointerdown', (e) => {
      const t = e.target as HTMLElement;
      const row = e.button === 0 ? (t.closest('[data-drag]') as HTMLElement | null) : null;
      if (!row || t.closest('button, input, a, .menu')) return;
      const [route, stop] = (row.dataset.drag ?? '').split('|');
      drag = { route, stop, row, x0: e.clientX, y0: e.clientY, live: false };
    });
    document.addEventListener('pointermove', (e) => {
      if (!drag) return;
      if (!drag.live) {
        if (Math.hypot(e.clientX - drag.x0, e.clientY - drag.y0) < 6) return;
        if (!start(e)) {
          drag = null;
          return;
        }
      }
      move(e);
    });
    document.addEventListener('pointerup', () => {
      if (drag?.live) {
        end(true);
        swallow = true;
        setTimeout(() => (swallow = false), 0);
      }
      drag = null;
    });
    // The click that ends a drag isn't a click on the row.
    document.addEventListener(
      'click',
      (e) => {
        if (!swallow) return;
        swallow = false;
        e.stopPropagation();
        e.preventDefault();
      },
      true,
    );
    document.addEventListener(
      'keydown',
      (e) => {
        if (e.key === 'Escape' && drag?.live) {
          e.preventDefault();
          e.stopPropagation();
          end(false);
        }
      },
      true,
    );
  }, []);
}

export const dragging = () => !!drag?.live;
